// src/utils/projectLabour.js
// ─────────────────────────────────────────────────────────────────────────
// Shared labour-allocation engine for Project Profitability and Project
// Cost Ledger. Both pages used to inline the same formula in slightly
// different shapes and produced divergent totals on real data, even though
// the code looked identical when read line-by-line. The only way to be
// certain the two reports agree on "how much labour did NG cost?" is for
// them to call the same function.
//
// Formula (per user-month with non-zero project hours):
//   totalCost (FTE)  = calcMonthlyCost(uid, year, month).totalCost
//                    = (base_salary + bonus) × salary_markup
//   proratedCost     = totalCost × baseContractedHours / 176
//   denominator      = max(totalLogged, effectiveContractedHours)
//   pctWorked        = denominator > 0 ? projectHours / denominator : 0
//   allocatedCost    = proratedCost × pctWorked
//
// Where:
//   baseContractedHours      profile.contractedHours (or 176 default)
//   effectiveContractedHours getEffectiveContractedHours() — base minus
//                            holidays and approved leave
//   totalLogged              sum of the user's hours across all projects
//                            in that month
//   projectHours             hours the user logged on this project that
//                            month
//
// The function returns aggregates the consumer pages need:
//   totalAllocated   grand total across all (user, month) for the project
//   byMonth          { 'YYYY-MM': allocatedCost }
//   byUser           { uid: { allocatedCost, projectHours, totalLogged,
//                             contractedHours (effective sum), months: [
//                               { ymKey, projectHours, totalLogged,
//                                 contractedHours (effective), normalizedTotal,
//                                 pctWorked, monthlyCost (prorated),
//                                 allocatedCost, costData } ] } }
// ─────────────────────────────────────────────────────────────────────────

const toNumericIdLocal = (id) => {
  if (typeof id === 'number') return id;
  if (typeof id === 'string' && /^-?\d+$/.test(id.trim())) return parseInt(id, 10);
  return id;
};

const parseYMLocal = (ymKey) => {
  if (!ymKey || typeof ymKey !== 'string') return null;
  const m = ymKey.match(/^(\d{4})-(\d{2})$/);
  if (!m) return null;
  return { year: parseInt(m[1], 10), month: parseInt(m[2], 10) };
};

export function computeProjectLabour({
  byProject,
  byUserMonth,
  projectId,
  months,
  getUserProfileAtDate,
  getEffectiveContractedHours,
  calcMonthlyCost,
  toNumericId = toNumericIdLocal,
  parseYM = parseYMLocal,
}) {
  const empty = { totalAllocated: 0, byMonth: {}, byUser: {} };
  const projEntries = byProject?.[projectId];
  if (!projEntries) return empty;
  if (!Array.isArray(months) || months.length === 0) return empty;

  const monthSet = new Set(months);
  const byMonth = {};
  const byUser = {};
  let totalAllocated = 0;

  for (const [uid, monthMap] of Object.entries(projEntries)) {
    const numUid = toNumericId(uid);
    let userAllocatedCost = 0;
    let userProjectHours = 0;
    let userTotalLogged = 0;
    let userContractedHours = 0;
    const userMonths = [];

    for (const ymKey of months) {
      if (!monthSet.has(ymKey)) continue;
      const projectHours = monthMap[ymKey] || 0;
      if (projectHours === 0) continue;
      const p = parseYM(ymKey);
      if (!p) continue;

      const totalLogged = byUserMonth?.[uid]?.[ymKey] || 0;
      const targetDate = `${p.year}-${String(p.month).padStart(2, '0')}-15`;
      const profile = getUserProfileAtDate ? getUserProfileAtDate(numUid, targetDate) : {};
      const baseContractedHours = (profile && profile.contractedHours) || 176;
      const effectiveContractedHours = getEffectiveContractedHours
        ? getEffectiveContractedHours(numUid, p.year, p.month)
        : baseContractedHours;

      const costData = calcMonthlyCost(numUid, p.year, p.month) || { totalCost: 0 };
      const proratedCost = (costData.totalCost || 0) * baseContractedHours / 176;
      const normalizedTotal = Math.max(totalLogged, effectiveContractedHours);
      const pctWorked = normalizedTotal > 0 ? projectHours / normalizedTotal : 0;
      const allocatedCost = proratedCost * pctWorked;

      byMonth[ymKey] = (byMonth[ymKey] || 0) + allocatedCost;
      totalAllocated += allocatedCost;
      userAllocatedCost += allocatedCost;
      userProjectHours += projectHours;
      userTotalLogged += totalLogged;
      userContractedHours += effectiveContractedHours;

      userMonths.push({
        ymKey,
        projectHours,
        totalLogged,
        contractedHours: effectiveContractedHours,
        normalizedTotal,
        pctWorked,
        monthlyCost: proratedCost,
        allocatedCost,
        costData,
      });
    }

    if (userMonths.length === 0) continue;
    byUser[uid] = {
      uid,
      numUid,
      allocatedCost: userAllocatedCost,
      projectHours: userProjectHours,
      totalLogged: userTotalLogged,
      contractedHours: userContractedHours,
      months: userMonths,
    };
  }

  return { totalAllocated, byMonth, byUser };
}

// ─────────────────────────────────────────────────────────────────────────
// computeInternalCostRedistribution
//
// Labour cost from internal projects (client = BRIO, e.g. "BRIO General")
// is treated as overhead the firm absorbs — it is pooled per office-month
// and redistributed to billable projects in proportion to each billable
// project's share of that office-month's billable hours.
//
// Both Profitability (internalCostByProjectMonth) and Cost Ledger surface
// this number; this helper is the single source of truth so the two pages
// cannot disagree on it.
//
// Inputs:
//   byProject, byUserMonth      from activeTimeEntries
//   activeUsers                 used only to map uid → office
//   internalProjectIds          Set of project IDs whose client is BRIO
//                               (or has no client) — these are the source
//                               of the pool
//   billableOfficeHours         { office: { ymKey: hours } } — sum of
//                               hours logged on non-internal projects
//                               per office-month (denominator)
//   projOfficeHours             { pid: { office: { ymKey: hours } } } —
//                               per project, per office-month hours
//   getUserProfileAtDate, getEffectiveContractedHours, calcMonthlyCost
//                               same helpers used elsewhere
//
// Returns: { pid: { ymKey: redistributedCostINR } }
// ─────────────────────────────────────────────────────────────────────────
export function computeInternalCostRedistribution({
  byProject,
  byUserMonth,
  activeUsers,
  internalProjectIds,
  billableOfficeHours,
  projOfficeHours,
  getUserProfileAtDate,
  getEffectiveContractedHours,
  calcMonthlyCost,
  toNumericId = toNumericIdLocal,
  parseYM = parseYMLocal,
}) {
  if (!internalProjectIds || typeof internalProjectIds.forEach !== 'function') return {};
  if (internalProjectIds.size === 0) return {};

  const userMap = {};
  (activeUsers || []).forEach(u => { userMap[toNumericId(u.id)] = u; });

  // 1. Pool internal-project labour cost by office-month
  const internalPool = {}; // { office: { ymKey: totalCostINR } }
  internalProjectIds.forEach(pid => {
    const projEntries = byProject?.[pid];
    if (!projEntries) return;
    Object.entries(projEntries).forEach(([uid, monthMap]) => {
      const numUid = toNumericId(uid);
      const office = (userMap[numUid]?.officeLocation || 'NL') === 'IN' ? 'IN' : 'NL';
      Object.entries(monthMap).forEach(([ymKey, projectHours]) => {
        if (projectHours <= 0) return;
        const p = parseYM(ymKey);
        if (!p) return;
        const totalLogged = byUserMonth?.[uid]?.[ymKey] || 0;
        const targetDate = `${p.year}-${String(p.month).padStart(2, '0')}-15`;
        const profile = getUserProfileAtDate ? getUserProfileAtDate(numUid, targetDate) : {};
        const baseCH = (profile && profile.contractedHours) || 176;
        const effCH = getEffectiveContractedHours
          ? getEffectiveContractedHours(numUid, p.year, p.month)
          : baseCH;
        const cost = calcMonthlyCost(numUid, p.year, p.month) || { totalCost: 0 };
        const proratedCost = (cost.totalCost || 0) * baseCH / 176;
        const normalized = Math.max(totalLogged, effCH);
        const allocated = normalized > 0 ? proratedCost * (projectHours / normalized) : 0;
        if (allocated <= 0) return;
        if (!internalPool[office]) internalPool[office] = {};
        internalPool[office][ymKey] = (internalPool[office][ymKey] || 0) + allocated;
      });
    });
  });

  // 2. Distribute pool to billable projects by hour share
  const result = {};
  Object.entries(internalPool).forEach(([office, monthPools]) => {
    Object.entries(monthPools).forEach(([ymKey, pool]) => {
      const totalBillable = billableOfficeHours?.[office]?.[ymKey] || 0;
      if (totalBillable <= 0 || pool <= 0) return;
      Object.entries(projOfficeHours || {}).forEach(([pid, pidOfficeMap]) => {
        const numPid = typeof pid === 'number' ? pid : parseInt(pid, 10);
        if (internalProjectIds.has(numPid)) return;
        const projHrs = pidOfficeMap?.[office]?.[ymKey] || 0;
        if (projHrs <= 0) return;
        const share = pool * (projHrs / totalBillable);
        if (!result[pid]) result[pid] = {};
        result[pid][ymKey] = (result[pid][ymKey] || 0) + share;
      });
    });
  });

  return result;
}
